import { useState } from 'react'
import { Box, ButtonBase, InputBase, Typography } from '@mui/material'
import { CheckBox } from '../../../../types/models/Presc'
import { Note } from './Note'
import { colorTokens } from '../../../../shared/styles/theme'
import { RADIUS } from '../../../../shared/styles/radii'
import { FieldEditor } from '../fields'

interface Props {
  /** 表示する文字列(コード値はラベル化済み)。 */
  display: string
  /** 編集の初期値(生の selected_option)。 */
  raw: string
  /** undefined = この項目は直せない(表示だけ)。 */
  editor?: FieldEditor
  /** editor が choice のときの候補。 */
  choices?: CheckBox[]
  /** 読み上げ用の項目名。 */
  label: string
  /** undefined = この項目は直せない。拒否したら「保存されなかった」の意味。 */
  onSave?: (value: string) => void | Promise<void>
  /** 送る前の形の検査。文字列を返したら保存せず、その文言を出したまま編集を続ける。 */
  validate?: (value: string) => string | null
  /** 編集中に出す入力の手引き(和暦など)。 */
  hint?: string
}

const VALUE_SX = {
  flex: 1,
  minWidth: 0,
  pt: '2px',
  fontSize: 14,
  color: colorTokens.fg.default,
  overflowWrap: 'anywhere' as const,
}

/** 読めなかった欄の字。値と同じ位置・同じ大きさで、色だけで沈める。 */
const PLACEHOLDER_SX = {
  color: colorTokens.fg.faint,
}

const SAVE_FAILED = '保存できませんでした'

/**
 * 直せない項目を click したときの返し。**理由は 1 つしかない** — 直せるかどうかは
 * `FIELD_EDITORS` だけが決めており、薬局や機種では変わらないため(fields.ts)。
 *
 * **「直せません」とは書かない**(松田 2026-09-03)。読み手に「**AI では改善できない**」と
 * 読まれる。画面が言っているのは**この欄を手で書き換えられるかどうか**だけで、
 * 読み取りの精度の話ではない。だから「編集できません」にする。
 */
const NOT_EDITABLE = 'この項目は編集できません(報告はできます)'

/**
 * 空の欄に置く字。**行も当たりも消さない** — 読めなかったことが情報で、そこが報告の対象になる。
 *
 * 直せる欄は `03-patterns/empty-states.md` 型 4「入力促し」だが、**表記は「未記入」ではなく `-`**
 * (松田 2026-09-03)。この面は紙と 1 項目ずつ突き合わせる版面で、16 行が縦に並ぶ中の
 * 空欄に語を置くと、読む対象でない字が増える。直せない欄は型 1 のまま「(なし)」で、
 * 同じ面の `ItemsPane` の `Empty` と同じ字。
 */
const EMPTY_EDITABLE = '-'
const EMPTY_READONLY = '(なし)'

/**
 * 項目の値。**click で枠なしのインライン入力に変わり、blur / Enter で保存・Esc で破棄**する
 * (03-patterns/inline-edit-gestures.md)。編集モードも保存ボタンも置かない。
 *
 * 直せる項目は ADR-0032 の範囲だけ(保険・患者情報・用法/コメント)。**直せない項目は click に
 * 理由で応える** — 静的な印で編集可否を示さないのは、hover に置いてよいのは補助操作という規則
 * (03-patterns/hover-only-chrome.md) に対して編集が主動線ではないため(本番実測で報告 472 回 :
 * 修正 14 件)。
 *
 * 保存 1 回で backend は処方箋の新しい版を作り、JAHIS CSV と QR を作り直す。**保存だけでは
 * レセコンに届かない**(届けるのはペイン末尾の「レセコンに送信」だけ・ADR-0033)ので、値の周りに
 * 送信を連想させる語を出さない。
 */
export const EditableValue = ({
  display,
  raw,
  editor,
  choices,
  label,
  onSave,
  validate,
  hint,
}: Props) => {
  const [draft, setDraft] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const isEditing = draft !== null
  // 空白だけの読み取りも「読めなかった」に寄せる(見た目が空なら当たりも消える)。
  const isEmpty = display.trim() === ''

  const close = () => {
    setDraft(null)
    setNote(null)
  }

  const commit = async (next: string) => {
    if (next === raw) return close()
    const message = validate?.(next) ?? null
    if (message) {
      setNote(message)
      return
    }
    try {
      await onSave?.(next)
      close()
    } catch {
      // backend は JAHIS CSV を作れない編集を400で丸ごと捨て、版を作らない(ADR-0032)。
      // 閉じると打った値が消えるうえ、通っていない値が画面に残る。
      setNote(SAVE_FAILED)
    }
  }

  if (!editor || !onSave) {
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-label={`${label}は編集できません`}
          onClick={() => setNote(NOT_EDITABLE)}
          // 行は項目キーで作られ処方箋を切り替えても作り直されないので、
          // 焦点が外れた時点で消す(別の紙に前の紙の文言が残らないように)。
          onBlur={() => setNote(null)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setNote(NOT_EDITABLE)
            }
          }}
          sx={{ ...VALUE_SX, cursor: 'default' }}
        >
          <Typography
            sx={{
              fontSize: 14,
              color: colorTokens.fg.default,
              ...(isEmpty ? PLACEHOLDER_SX : {}),
            }}
          >
            {isEmpty ? EMPTY_READONLY : display}
          </Typography>
        </Box>
        {note && <Note>{note}</Note>}
      </Box>
    )
  }

  if (isEditing && editor === 'choice') {
    return (
      <Box sx={{ ...VALUE_SX, pt: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(choices ?? []).map((choice) => (
            <ButtonBase
              key={choice.value}
              onClick={() => commit(choice.value)}
              sx={{
                px: 1,
                height: 24,
                fontSize: 13,
                borderRadius: `${RADIUS.sm}px`,
                border: `1px solid ${colorTokens.border.default}`,
                color:
                  choice.value === raw
                    ? colorTokens.fg.default
                    : colorTokens.fg.muted,
                background:
                  choice.value === raw
                    ? colorTokens.bg.emphasis
                    : colorTokens.bg.default,
                '&:hover': { background: colorTokens.overlay.hover },
              }}
            >
              {choice.label}
            </ButtonBase>
          ))}
          <ButtonBase
            aria-label={`${label}の編集をやめる`}
            onClick={close}
            sx={{
              px: 1,
              height: 24,
              fontSize: 12,
              color: colorTokens.fg.faint,
            }}
          >
            やめる
          </ButtonBase>
        </Box>
        {note && <Note>{note}</Note>}
      </Box>
    )
  }

  if (isEditing) {
    return (
      <Box sx={{ ...VALUE_SX, pt: 0 }}>
        <InputBase
          autoFocus
          value={draft}
          inputProps={{ 'aria-label': `${label}を編集` }}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => commit(draft.trim())}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commit(draft.trim())
            }
            if (event.key === 'Escape') close()
          }}
          sx={{
            width: '100%',
            fontSize: 14,
            color: colorTokens.fg.default,
            '& input': { p: 0 },
          }}
        />
        {(note ?? hint) && <Note>{note ?? hint ?? ''}</Note>}
      </Box>
    )
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${label}を編集`}
      onClick={() => setDraft(raw)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setDraft(raw)
        }
      }}
      sx={{
        ...VALUE_SX,
        borderRadius: `${RADIUS.sm}px`,
        cursor: 'text',
        '&:hover': { background: colorTokens.overlay.hover },
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          color: colorTokens.fg.default,
          ...(isEmpty ? PLACEHOLDER_SX : {}),
        }}
      >
        {isEmpty ? EMPTY_EDITABLE : display}
      </Typography>
    </Box>
  )
}
